import {useCallback, useState} from "react";

import {useQueryClient} from "@tanstack/react-query";
import {RowSelectionState} from "@tanstack/react-table";
import {AxiosError} from "axios";

import {
  ActionRecordModel,
  ActionTypeModel,
  CustomActionResultListModel,
} from "@/api/models";
import {Action} from "@/api/services";
import {
  getGetActionListSummaryQueryKey,
  useCreateAction,
  useDeleteAction,
  useGetAction,
  useUpdateAction,
} from "@/api/services/queries";
import {actionTransform} from "@/api/transform/actions";
import {TOAST_MESSAGES} from "@/constants/toastMessages";
import {useToast} from "@/hooks/useToast";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";

export type UseActionDetailsManageProps = {
  id: ActionRecordModel["action_id"];
  actionType: ActionTypeModel;
};

export const useActionDetailsManage = <
  T extends ActionRecordModel = ActionRecordModel,
>(
  props: UseActionDetailsManageProps,
) => {
  const {id, actionType} = props;
  const [apiResponse, setApiResponse] =
    useState<CustomActionResultListModel | null>(null);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [onlySelected, setOnlySelected] = useState(false);
  const selectedWorkspace = FlowBuilderSettings.workspaceId;

  const queryClient = useQueryClient();

  const response = useGetAction<T>(id, {
    query: {
      queryKey: ["actions", selectedWorkspace, actionType, id],
      select: (dto: Action) => {
        return actionTransform[actionType].makeRecordModel(dto as never) as T;
      },
      enabled: !!id,
    },
  });

  const {successToast, errorToast} = useToast();

  const {mutateAsync: updateAction} = useUpdateAction();
  const {mutateAsync: createAction} = useCreateAction();

  const refreshOne = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["actions", selectedWorkspace, actionType, id],
    });
    queryClient.invalidateQueries({
      queryKey: ["assistant-action-details", id],
    });
  }, [actionType, id, queryClient, selectedWorkspace]);

  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["actions", selectedWorkspace, actionType],
    });
  }, [actionType, queryClient, selectedWorkspace]);

  const refreshListAndSummary = useCallback(() => {
    refreshList();
    queryClient.invalidateQueries({
      queryKey: getGetActionListSummaryQueryKey({
        workspace_id: selectedWorkspace,
      }),
    });
  }, [refreshList, queryClient, selectedWorkspace]);

  const handleUpdateAction = useCallback(
    async (
      data: Parameters<
        (typeof actionTransform)[typeof actionType]["makeUpdateDto"]
      >[0],
    ) => {
      // TODO: fix this
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const dto = actionTransform[actionType].makeUpdateDto(data);

      const result = await updateAction({slug: id, data: dto});

      successToast(TOAST_MESSAGES.ACTION_UPDATED);
      refreshOne();
      refreshList();

      return result;
    },
    [actionType, id, refreshOne, refreshList, successToast, updateAction],
  );

  const {mutateAsync: deleteAction} = useDeleteAction({
    mutation: {
      onError: (error: AxiosError<any>) => {
        if (error.response?.status === 406) {
          errorToast(error.response?.data.message);
        } else {
          throw error;
        }
      },
    },
  });

  const handleDeleteAction = useCallback(async () => {
    if (!id) return;
    try {
      await deleteAction({slug: id});
      successToast(TOAST_MESSAGES.ACTION_DELETED);
      refreshListAndSummary();
    } catch {
      // error is handled by useDeleteAction's onError
    }
  }, [id, deleteAction, successToast, refreshListAndSummary]);

  const handleCreateAction = useCallback(
    async (
      data: Parameters<
        (typeof actionTransform)[typeof actionType]["makeCreateDto"]
      >[0],
    ) => {
      // TODO: fix this
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const dto = actionTransform[actionType].makeCreateDto(data);
      const action = await createAction({data: dto});
      successToast(TOAST_MESSAGES.ACTION_CREATED);
      refreshListAndSummary();
      return action;
    },
    [actionType, createAction, successToast, refreshListAndSummary],
  );
  return {
    id,
    ...response,
    handleDeleteAction,
    handleUpdateAction,
    handleCreateAction,
    apiResponse,
    setApiResponse,
    selectedRows,
    setSelectedRows,
    onlySelected,
    setOnlySelected,
  } as const;
};
